import React, { Suspense, lazy, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Navigate,
  Route,
  Routes,
  matchPath,
  useLocation,
} from 'react-router-dom'; 
import { useAuth } from './auth/AuthContext';
import SessionExpiredDialog from './components/CustomDialogModal/SessionExpiredDialog';
import Layout from './components/Layout/Layout';
import Loading from './components/Loading/Loading';
import LoginCallback from './components/LoginCallback/LoginCallback';
import LoginRedirect from './components/LoginRedirect/LoginRedirect';
import LogoutCallback from './components/LogoutCallback/LogoutCallback';
import { PATHS } from './constants';
import ErrorPage from './containers/ErrorPage/ErrorPage';
import MainBackground from './images/Main_Background.svg';
import { sessionStore } from './utils/sessionStore';

const Signup = lazy(() => import('./containers/Signup/Signup'));
const Dashboard = lazy(() => import('./containers/Dashboard/Dashboard'));
const ApplicantForm = lazy(
  () => import('./containers/ApplicantForm/ApplicantForm')
);
const ApplicationForm = lazy(
  () => import('./containers/ApplicationForm/ApplicationForm')
);
const MyApplications = lazy(
  () => import('./containers/MyApplications/MyApplications')
);
const ApplicationLayout = lazy(
  () => import('./containers/Application/ApplicationLayout')
);
const ApplicationDetails = lazy(
  () =>
    import('./containers/Application/ApplicationDetails/ApplicationDetails')
);
const AmendmentForm = lazy(
  () => import('./containers/Application/AmendmentForm/AmendmentForm')
);
const CorrectionRequestForm = lazy(
  () => import('./containers/CorrectionRequestForm/CorrectionRequestForm')
);
const MyAccount = lazy(() => import('./containers/MyAccount/MyAccount'));
const UpdateAccountDetails = lazy(
  () => import('./containers/UpdateAccountDetails/UpdateAccountDetails')
);

const App: React.FC = () => {
  const { isAuthenticated, authLoading } = useAuth();
  const [sessionExpired, setSessionExpired] = useState(false);

  const { logout } = useAuth();

  useEffect(() => {
    sessionStore.setSessionExpiredHandler(() => {
      setSessionExpired(true);
    });
  }, []);

  const location = useLocation();

  const withMainBackgroundRoutes = [
    PATHS.APPLICANT.DASHBOARD,
    PATHS.APPLICANT.APPLICATION_DETAILS_ROUTE,
    PATHS.APPLICANT.MY_APPLICATIONS,
  ];
  const background = withMainBackgroundRoutes.some((route) =>
    matchPath({ path: route, end: true }, location.pathname)
  )
    ? `url(${MainBackground})`
    : 'none';

  const fullWidthRoutes = [
    PATHS.APPLICANT.NEW_APPLICANT,
    PATHS.APPLICANT.NEW_APPLICATION_WITH_APPLICANT,
    PATHS.APPLICANT.MY_ACCOUNT,
    PATHS.APPLICANT.CORRECTION_REQUEST_ROUTE,
    PATHS.APPLICANT.AMEND_APPLICATION_ROUTE,
  ];
  const isFullWidth = fullWidthRoutes.some((route) =>
    matchPath({ path: route, end: false }, location.pathname)
  );

  if (authLoading) {
    return <Loading />;
  }

  return (
    <Suspense fallback={<Loading />}>
      <ErrorBoundary FallbackComponent={() => <ErrorPage />}>
        <SessionExpiredDialog
          open={sessionExpired}
          onLogin={() => {
            setSessionExpired(false);
            logout();
          }}
        />
        <Routes>
          {/* Public routes */}
          <Route path={PATHS.AUTH.CALLBACK} element={<LoginCallback />} />
          <Route
            path={PATHS.AUTH.LOGOUT_CALLBACK}
            element={<LogoutCallback />}
          />
          <Route
            path={PATHS.AUTH.SIGNUP}
            element={
              <Layout background={background}>
                <Signup />
              </Layout>
            }
          />
          <Route
            path={PATHS.UNAUTH.NOT_FOUND}
            element={
              <Layout background={background}>
                <ErrorPage errorType="404" />
              </Layout>
            }
          />
          <Route
            path={PATHS.UNAUTH.ERROR}
            element={
              <Layout background={background}>
                <ErrorPage errorType="loading-error" />
              </Layout>
            }
          />

          {/* Protected routes */}
          <Route
            element={
              isAuthenticated ? (
                <Layout background={background} isFullWidth={isFullWidth} />
              ) : (
                <LoginRedirect />
              )
            }
          >
            <Route path={PATHS.APPLICANT.DASHBOARD} element={<Dashboard />} />
            <Route
              path={PATHS.APPLICANT.NEW_APPLICANT}
              element={<ApplicantForm />}
            />
            <Route
              path={PATHS.APPLICANT.NEW_APPLICATION}
              element={<ApplicationForm />}
            />
            <Route
              path={PATHS.APPLICANT.MY_APPLICATIONS}
              element={<MyApplications />}
            />
            {/* Nested routes under /application with shared context */}
            <Route
              path={PATHS.APPLICANT.APPLICATION_DETAILS_ROUTE}
              element={<ApplicationLayout />}
            >
              <Route index element={<ApplicationDetails />} />
              <Route
                path={PATHS.APPLICANT.AMEND_APPLICATION_ROUTE.split('/').pop()}
                element={<AmendmentForm />}
              />
              <Route
                path={PATHS.APPLICANT.CORRECTION_REQUEST_ROUTE.split('/').pop()}
                element={<CorrectionRequestForm />}
              />
            </Route>
            <Route path={PATHS.APPLICANT.MY_ACCOUNT} element={<MyAccount />} />
            <Route
              path={PATHS.APPLICANT.UPDATE_ACCOUNT_DETAILS}
              element={<UpdateAccountDetails />}
            />
          </Route>

          {/* Keep wildcard last so it does not compete with protected routes */}
          <Route
            path={PATHS.UNAUTH.WILDCARD}
            element={<Navigate to={PATHS.UNAUTH.NOT_FOUND} replace />}
          />
        </Routes>
      </ErrorBoundary>
    </Suspense>
  );
};

export default App;
